import { useState } from 'react';
import { IconMessage, IconPin, IconSparkles } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Table, type Column } from '@hana/ui';
import { AttnPanel, BarsPanel, Card, Chart12, ListPanel, PctChip, type Tone } from './panels';
import { JOB, MS, fi, pc, sum } from './data';
import b from './bod.module.css';
import s from './ov.module.css';
import './i18nOv';

// Mint-BOD · tab "Tổng quan" và "Nhân sự" (không tiền; tab tiền nằm sau quyền money). Dữ liệu mẫu chép từ code board.
const HC = [33, 33, 34, 34, 34, 35, 35, 35, 35, 36, 35, 36], LATE = [7, 4, 6, 3, 5, 6, 4, 5, 3, 6, 3, 5], OT = [52, 61, 48, 30, 58, 55, 47, 62, 70, 66, 49, 64];
const ONT = [93, 95, 94, 96, 92, 95, 94, 95, 96, 95, 95, 94];
const JLY = [470, 455, 430, 380, 450, 470, 440, 400, 420, 410, 350, 380];
const NEWC = [6, 4, 5, 3, 4, 5, 3, 4, 4, 3, 2, 4];
const TEU = JOB.map((n) => Math.round(n * 1.62));
const YI = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const YP = 295 / 365, QP = 22 / 92;
const CW = 720, CH = 180, SW = 60;

const spark = (a: readonly number[]): string => {
  const mn = Math.min(...a), r = Math.max(...a) - mn || 1;
  return a.map((v, i) => `${i ? 'L' : 'M'}${(2 + (i * 116) / (a.length - 1)).toFixed(1)} ${(21 - ((v - mn) / r) * 18).toFixed(1)}`).join(' ');
};
const TXT: Record<Tone, string> = { ok: s.tOk!, warn: s.tWarn!, err: s.tErr!, pri: '' };

type Pt = { id: string; k: string; v: string; u?: string; sub?: string; tone?: Tone; sp?: readonly number[] };
const TILES: Record<string, Pt> = {
  'ov.job': { id: 'ov.job', k: 'bod.ov.job', v: fi(sum(JOB, [11])), sub: 'bod.ov.jobSub', tone: 'ok', sp: JOB },
  'ov.hc': { id: 'ov.hc', k: 'bod.ov.hc', v: '36', u: 'bod.ov.people', sub: 'bod.ov.hcSub' },
  'sla.ok': { id: 'sla.ok', k: 'bod.sla.k.ok', v: '92%', sub: 'bod.ov.slaSub', tone: 'warn' },
  'fleet.run': { id: 'fleet.run', k: 'bod.ov.fleet', v: '14/16', sub: 'bod.ov.fleetSub' },
  'hr.now': { id: 'hr.now', k: 'bod.hr.k.now', v: '36', u: 'bod.ov.people', sp: HC },
  'hr.in': { id: 'hr.in', k: 'bod.hr.k.in', v: '2', sub: 'bod.hr.k.inSub' },
  'hr.out': { id: 'hr.out', k: 'bod.hr.k.out', v: '1', sub: 'bod.hr.k.outSub' },
  'hr.late': { id: 'hr.late', k: 'bod.hr.k.late', v: '5', u: 'bod.hr.turn', sub: 'bod.hr.k.lateSub', tone: 'warn', sp: LATE },
  'hr.leave': { id: 'hr.leave', k: 'bod.hr.k.leave', v: '61%', sub: 'bod.hr.k.leaveSub' },
  'hr.review': { id: 'hr.review', k: 'bod.hr.k.review', v: '21/36', sub: 'bod.hr.k.reviewSub' },
  'fleet.ontime': { id: 'fleet.ontime', k: 'bod.fl.k.ontime', v: '94%', sub: 'bod.fl.k.ontimeSub', sp: ONT },
  'sla.chat': { id: 'sla.chat', k: 'bod.sla.k.chat', v: '1', sub: 'GREENBUD · Hei', tone: 'warn' },
};

function Tiles({ ids, pins, onPin, onAsg, n, sub }: { ids: readonly string[]; pins: readonly string[]; onPin: (id: string) => void; onAsg: (k: string) => void; n?: number; sub?: boolean }) {
  const t = useT();
  const tr = (x?: string) => (x && x.startsWith('bod.') ? t(x) : x ?? '');
  return (
    <div className={`${s.pins} ${sub ? s.sub : ''}`} style={{ gridTemplateColumns: `repeat(${n ?? ids.length}, minmax(0, 1fr))` }}>
      {ids.map((id) => {
        const x = TILES[id]!, on = pins.includes(id), k = t(x.k);
        const pk = t(on ? 'bod.ov.unpin' : 'bod.ov.pin', { k });
        return (
          <div key={id} className={s.pt}>
            <div className={s.ptHead}>
              <span>{k}</span>
              <button type="button" className={s.ib} aria-label={pk} title={pk} aria-pressed={on} onClick={() => onPin(id)}><IconPin size={15} aria-hidden="true" /></button>
              <button type="button" className={s.ib} aria-label={t('bod.ov.asg', { k })} title={t('bod.ov.asgT')} aria-haspopup="dialog" onClick={() => onAsg(k)}><IconMessage size={15} aria-hidden="true" /></button>
            </div>
            <b className={s.ptV}>{x.v}{x.u && <small>{tr(x.u)}</small>}</b>
            <div className={`${s.ptS} ${x.tone ? TXT[x.tone] : ''}`}>{tr(x.sub)}</div>
            {x.sp && <svg role="img" aria-label={t('bod.spark', { k, a: MS[0]!, b: MS[11]! })} width="100%" height="24" viewBox="0 0 120 24" preserveAspectRatio="none"><path d={`${spark(x.sp)} L118 24 L2 24 Z`} className={b.spA} /><path d={spark(x.sp)} className={b.spL} vectorEffect="non-scaling-stroke" /></svg>}
          </div>
        );
      })}
    </div>
  );
}

function Goal({ k, sub, a, tg, pace, ly }: { k: string; sub: string; a: number; tg: number; pace: number; ly: number }) {
  const t = useT();
  const p = a / tg, ok = p >= pace - 0.02, mid = !ok && p >= pace - 0.08;
  const d = ((a - ly) / Math.abs(ly)) * 100;
  const cls = ok ? s.gok : mid ? s.gwarn : s.gerr;
  const chip = ok ? 'bod.ov.g.ok' : mid ? 'bod.ov.g.mid' : 'bod.ov.g.bad';
  const pct = t('bod.ov.g.pct', { p: pc(p * 100), t: Math.round(pace * 100) });
  return (
    <div className={s.goal}>
      <div className={s.gh}><span>{k}</span><span>{sub}</span><b>{fi(a)}</b><span>/ {fi(tg)}</span></div>
      <div role="img" aria-label={`${k}: ${fi(a)} / ${fi(tg)}, ${pct}`} className={s.gbar}><div className={cls} style={{ width: `${Math.min(100, p * 100).toFixed(1)}%` }} /><div className={s.gmk} title={t('bod.ov.g.mk')} style={{ left: `${(pace * 100).toFixed(1)}%` }} /></div>
      <div className={s.gf}>
        <span className={`${s.chip} ${ok ? s.cok : mid ? s.cwarn : s.cerr}`}>{t(chip)}</span>
        <span>{pct}</span>
        <span className={d >= 0 ? b.up : b.down}>{t('bod.ov.g.yoy', { v: fi(ly), d: `${d >= 0 ? '+' : '−'}${Math.abs(d).toFixed(1).replace('.', ',')}%` })}</span>
      </div>
    </div>
  );
}

const ASKS: readonly (readonly [string, string, readonly (readonly [string, string])[]])[] = [
  ['bod.ov.q1', 'bod.ov.a1', [['Lys · Hoạt động KD', '#ops'], ['Ezy · Danh sách Job', '/ezy/jobs']]],
  ['bod.ov.q2', 'bod.ov.a2', [['Mee · Chấm công', '/mee'], ['Lys · Nhân sự', '#hr']]],
  ['bod.ov.q3', 'bod.ov.a3', [['Box · support@', '/box'], ['Lys · SLA', '#sla']]],
  ['bod.ov.q4', 'bod.ov.a4', [['Mov · Đội xe', '/mov'], ['Lys · Đội xe', '#fleet']]],
];

function AskPanel({ onAsg }: { onAsg: (k: string) => void }) {
  const t = useT();
  const [i, setI] = useState<number | null>(null);
  const [q, setQ] = useState('');
  const send = () => { const v = q.trim(); if (!v) return; const j = ASKS.findIndex((x) => t(x[0]).toLowerCase() === v.toLowerCase()); setI(j); setQ(''); };
  const a = i == null ? null : i >= 0 ? ASKS[i]! : null;
  return (
    <Card title="Ask HANA" sub={t('bod.ov.askS')} span={4}>
      <div className={s.sugg}>{ASKS.map((x, j) => <button key={x[0]} type="button" aria-pressed={i === j} onClick={() => { setI(j); setQ(''); }}>{t(x[0])}</button>)}</div>
      <div className={s.askRow}>
        <input aria-label={t('bod.ov.askIn')} placeholder={t('bod.ov.askPh')} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }} />
        <button type="button" onClick={send}><IconSparkles size={16} aria-hidden="true" />{t('bod.ov.askGo')}</button>
      </div>
      {i != null ? (
        <div className={s.ans} aria-live="polite">
          <b>{a ? t(a[0]) : t('bod.ov.free')}</b>
          <div>{a ? t(a[1]) : t('bod.ov.freeA')}</div>
          <div className={s.srcs}><span>{t('bod.ov.src')}</span>{(a ? a[2] : [['Lys · Tổng quan', '#ov'] as const]).map(([k, h]) => <a key={k} href={h}>{k}</a>)}</div>
          <div className={s.gf}><span style={{ flex: 1, color: 'var(--lb)' }}>{t('bod.ov.ro')}</span><button type="button" className={s.act} onClick={() => onAsg(a ? t(a[0]) : t('bod.ov.free'))}>{t('bod.ov.asgFrom')}</button></div>
        </div>
      ) : <div className={s.hint}>{t('bod.ov.askHint')}</div>}
    </Card>
  );
}

type AR = readonly [string, string, string, Tone, string];
function AttnList({ title, sub, span, rows, onAsg }: { title: string; sub: string; span: 7 | 6 | 5; rows: readonly AR[]; onAsg: (k: string) => void }) {
  const t = useT();
  const C: Record<Tone, string> = { err: 'var(--err)', warn: 'var(--warn)', pri: 'var(--pri)', ok: 'var(--ok)' };
  return (
    <section aria-label={title} className={b.card} style={{ gridColumn: `span ${span}` }}>
      <div className={b.ph}><h2>{title}</h2><span>{sub}</span></div>
      <div>
        {rows.map(([k, app, v, sev, sb]) => (
          <div key={k} className={s.attn}>
            <i className={s.dotc} style={{ background: C[sev] }} aria-hidden="true" />
            <a href="#" title={t('bod.ov.openAt', { app })}><span>{k}</span><span>{sb}</span></a>
            <span className={s.app}>{app}</span>
            <b className={s.av} style={{ color: sev === 'err' ? 'var(--err)' : sev === 'warn' ? 'var(--warn)' : undefined }}>{v}</b>
            <button type="button" className={s.ab} aria-label={t('bod.ov.asgOf', { k })} title={t('bod.ov.asgT')} aria-haspopup="dialog" onClick={() => onAsg(k)}><IconMessage size={15} aria-hidden="true" /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

function Trend({ idx }: { idx: readonly number[] }) {
  const t = useT();
  const mx = Math.max(...JOB, ...JLY) * 1.12;
  const y = (v: number) => (CH - (Math.max(0, v) / mx) * (CH - 12)).toFixed(1);
  const bars = JOB.map((v, i) => { const h = (v / mx) * (CH - 12); return `M${(i * SW + 12).toFixed(1)} ${y(v)}h36v${h.toFixed(1)}h-36z `; }).join('');
  const line = JLY.map((v, i) => `${i ? 'L' : 'M'}${(i * SW + SW / 2).toFixed(1)} ${y(v)}`).join(' ');
  return (
    <Card title={t('bod.ov.trend')} sub={t('bod.ov.trendS')} span={8}>
      <div className={b.leg}><span><i className={b.fOb} />Job</span><span><i className={b.mk} />{t('bod.ov.ly')}</span><em>{t('bod.ov.peak', { v: fi(mx) })}</em></div>
      <svg role="img" aria-label={`${t('bod.ov.trendA')}: ${MS.map((m, i) => `${m} ${fi(JOB[i]!)} / ${fi(JLY[i]!)}`).join(', ')}`} width="100%" height="180" viewBox={`0 0 ${CW} ${CH}`} preserveAspectRatio="none">
        <path d={idx.map((i) => `M${i * SW + 2} 0h56v${CH}h-56z`).join(' ')} className={b.hl} />
        <path d={[0.25, 0.5, 0.75, 1].map((q) => `M0 ${(CH - q * (CH - 12)).toFixed(1)}H${CW}`).join(' ') + ` M0 ${CH - 0.5}H${CW}`} className={b.gridL} vectorEffect="non-scaling-stroke" />
        <path d={bars} className={b.fOb} />
        <path d={line} className={s.lineO} vectorEffect="non-scaling-stroke" />
      </svg>
      <div className={b.xl} aria-hidden="true">{MS.map((k, i) => <span key={k} className={idx.includes(i) ? b.xon : ''}>{k}</span>)}</div>
    </Card>
  );
}

function Pins({ pins, onPin, onAsg }: { pins: readonly string[]; onPin: (id: string) => void; onAsg: (k: string) => void }) {
  const t = useT();
  const ids = pins.filter((id) => TILES[id]);
  return (
    <Card title={t('bod.ov.pinned')} sub={ids.length ? t('bod.ov.pinnedS', { n: ids.length }) : ''} span={12}>
      {ids.length ? <Tiles ids={ids} pins={pins} onPin={onPin} onAsg={onAsg} n={Math.min(6, Math.max(4, ids.length))} sub /> : <div className={s.note}>{t('bod.ov.pinNone')}</div>}
    </Card>
  );
}

function usePins(onAsg: () => void, notify: (m: string) => void) {
  const t = useT();
  const [pins, setPins] = useState<string[]>(['hr.late', 'fleet.ontime', 'sla.chat']);
  const pin = (id: string) => {
    if (pins.includes(id)) { setPins(pins.filter((x) => x !== id)); return; }
    if (pins.length >= 6) { notify(t('bod.ov.pinMax')); return; }
    setPins([...pins, id]);
  };
  return { pins, pin, asg: onAsg };
}

export function OvTab({ idx, onLater, notify }: { idx: readonly number[]; onLater: () => void; notify: (m: string) => void }) {
  const t = useT();
  const { pins, pin } = usePins(onLater, notify);
  const jy = sum(JOB, YI), ty = sum(TEU, YI), ny = sum(NEWC, YI);
  const asg = () => onLater();
  const A: AR[] = [
    [t('bod.ov.a.loss'), 'Ezy · Job', '8', 'err', t('bod.ov.a.lossS')],
    [t('bod.ov.a.cut'), 'Ezy · Job', '6', 'warn', t('bod.ov.a.cutS')],
    [t('bod.ov.a.mail'), 'Box', '3', 'err', 'support@ 2 · trucking@ 1 · ' + t('bod.sla.k.today')],
    [t('bod.ov.a.chat'), 'Hei', '1', 'warn', 'GREENBUD ' + t('bod.ov.chatS')],
    [t('bod.ov.a.hd'), 'Mee', '2', 'warn', 'HoaTU · NhiDTT'],
    [t('bod.ov.a.xe'), 'Mov', '1', 'warn', t('bod.ov.xeS')],
    [t('bod.ov.a.kpe'), 'Lys', '1', 'pri', t('bod.tab.evt')],
  ];
  return (
    <div className={b.grid}>
      <Pins pins={pins} onPin={pin} onAsg={asg} />
      <section aria-label={t('bod.sec.metrics')} className={b.span12}><Tiles ids={['ov.job', 'ov.hc', 'sla.ok', 'fleet.run']} pins={pins} onPin={pin} onAsg={asg} /></section>
      <Card title={t('bod.ov.gY')} sub={t('bod.ov.gYS')} span={6} acts={<button type="button" className={s.act} onClick={asg}>{t('bod.ov.setGoal')}</button>}>
        <div><Goal k="Job" sub={t('bod.ov.y2026')} a={jy} tg={5600} pace={YP} ly={4120} /><Goal k="TEU" sub={t('bod.ov.teu')} a={ty} tg={9200} pace={YP} ly={6880} /><Goal k={t('bod.ov.newc')} sub={t('bod.ov.newcS')} a={ny} tg={50} pace={YP} ly={41} /></div>
      </Card>
      <Card title={t('bod.ov.gQ')} sub={t('bod.ov.gQS')} span={6}>
        <div><Goal k="Job" sub="Q4/2026" a={sum(JOB, [11])} tg={1650} pace={QP} ly={380} /><Goal k="TEU" sub={t('bod.ov.teu')} a={sum(TEU, [11])} tg={2700} pace={QP} ly={610} /><Goal k={t('bod.ov.newc')} sub={t('bod.ov.newcS')} a={sum(NEWC, [11])} tg={14} pace={QP} ly={3} /></div>
      </Card>
      <Trend idx={idx} />
      <AskPanel onAsg={asg} />
      <AttnList title={t('bod.ov.attn')} sub={t('bod.ov.attnS')} span={7} rows={A} onAsg={asg} />
      <ListPanel title={t('bod.ov.cmp')} sub={t('bod.ov.cmpS')} span={5} rows={[['Job', '+12%', 'ok'], [t('bod.ov.cmpJob'), `${fi(JOB[11]!)} / ${fi(JLY[11]!)} · +${(((JOB[11]! - JLY[11]!) / JLY[11]!) * 100).toFixed(1).replace('.', ',')}%`, 'ok'], [t('bod.ov.cmpCus'), '+4'], [t('bod.ov.cmpMail'), t('bod.ov.cmpMailV'), 'ok'], [t('bod.ov.cmpLate'), t('bod.ov.cmpLateV'), 'warn'], [t('bod.ov.cmpFl'), t('bod.ov.cmpFlV'), 'warn']]} />
      <Card title={t('bod.ov.asgP')} sub={t('bod.ov.asgPS')} span={6}>
        <div className={b.list}>
          {[['SLA · PHILIP', t('bod.ov.asgD1')], ['Đăng kiểm · PHONGNT', t('bod.ov.asgD2')]].map(([k, v], i) => <div key={k} style={{ borderTop: i ? undefined : '0' }}><span>{k}</span><b>{v}</b></div>)}
        </div>
      </Card>
      <Card title={t('bod.ov.news')} sub={t('bod.ov.newsS')} span={6} acts={<><button type="button" className={s.act} onClick={asg}>{t('bod.ov.test')}</button><button type="button" className={s.act} onClick={asg}>{t('bod.ov.chg')}</button></>}><div className={b.list}>{[[t('bod.ov.nw1'), t('bod.ov.nw1v')], [t('bod.ov.nw2'), t('bod.ov.nw2v')], [t('bod.ov.nw3'), t('bod.ov.nw3v')], [t('bod.ov.nw4'), '[BOD-DIR] PHILIP · DAVID'], [t('bod.ov.nw5'), t('bod.ov.nw5v')]].map(([k, v], i) => <div key={k} style={{ borderTop: i ? undefined : '0' }}><span>{k}</span><b>{v}</b></div>)}</div></Card>
    </div>
  );
}

type DR = readonly [string, number, number, string, number, number, number, string];
const DR: readonly DR[] = [['SALE · kinh doanh', 9, 1, '58%', 6, 1, 0, '56 job / người'], ['OPS · vận hành', 9, 1, '64%', 31, 1, 1, '18,4 job / người'], ['CS · chăm sóc khách', 8, 3, '61%', 14, 2, 1, '42 mail / ngày'], ['ACC · kế toán', 4, 0, '55%', 9, 1, 0, '310 chứng từ / người'], ['BKG · booking', 4, 0, '70%', 4, 0, 0, '125 booking / người'], ['HR · nhân sự', 2, 0, '50%', 0, 0, 0, '—']];
const DL: readonly (readonly [string, number])[] = [['CS · chăm sóc khách', 3], ['OPS · vận hành', 1], ['SALE · kinh doanh', 1], ['ACC · kế toán', 0], ['BKG · booking', 0], ['HR · nhân sự', 0]];

export function HrTab({ idx, onLater, notify }: { idx: readonly number[]; onLater: () => void; notify: (m: string) => void }) {
  const t = useT();
  const { pins, pin } = usePins(onLater, notify);
  const cols: Column<DR>[] = [
    { key: 'd', header: t('bod.hr.c.dept'), width: '20%', cell: (r) => <b>{r[0]}</b> },
    { key: 'n', header: t('bod.hr.c.n'), width: '9%', align: 'right', cell: (r) => fi(r[1]) },
    { key: 'l', header: t('bod.hr.c.late'), width: '10%', align: 'right', cell: (r) => (r[2] > 2 ? <PctChip v={String(r[2])} kind="warn" /> : String(r[2])) },
    { key: 'p', header: t('bod.hr.c.leave'), width: '12%', align: 'right', cell: (r) => r[3] },
    { key: 'o', header: t('bod.hr.c.ot'), width: '12%', align: 'right', cell: (r) => fi(r[4]) },
    { key: 'e', header: t('bod.hr.c.err'), width: '12%', align: 'right', cell: (r) => <span className={r[5] ? '' : b.mute}>{r[5]}</span> },
    { key: 'r', header: t('bod.hr.c.rew'), width: '10%', align: 'right', cell: (r) => <span className={r[6] ? b.up : b.mute}>{r[6]}</span> },
    { key: 'ns', header: t('bod.hr.c.prod'), width: '15%', align: 'right', cell: (r) => <span style={{ color: 'var(--tx2)' }}>{r[7]}</span> },
  ];
  const A: AR[] = [
    [t('bod.hr.a1'), 'Mee · Chấm công', '3', 'warn', t('bod.hr.a1s')],
    [t('bod.ov.a.hd'), 'Mee', '2', 'warn', 'HoaTU · NhiDTT'],
    [t('bod.hr.a3'), 'Mee', '4', 'warn', t('bod.hr.a3s')],
    [t('bod.hr.a4'), 'Mee · Đánh giá', '15', 'pri', t('bod.hr.a4s')],
    [t('bod.hr.a5'), 'Mee', '8%', 'ok', t('bod.hr.a5s')],
  ];
  const ppl = [['MAITTN', 2, 'warn', 'CS · phép còn 9 ngày'], ['HOATTN', 1, 'pri', 'CS · HĐLĐ hết hạn 18/Nov'], ['ANHLT', 1, 'pri', 'OPS · phép còn 12 ngày'], ['TUANNT', 1, 'pri', 'SALE · phép còn 6 ngày']] as const;
  return (
    <div className={b.grid}>
      <div className={b.span12}><Tiles ids={['hr.now', 'hr.in', 'hr.out', 'hr.late', 'hr.leave', 'hr.review']} pins={pins} onPin={pin} onAsg={onLater} /></div>
      <Chart12 title={t('bod.hr.ch1')} sub={t('bod.hr.ch1s')} span={8} ser={HC} label={t('bod.ov.hc')} max={40} ymax={t('bod.hr.ch1y')} idx={idx} />
      <BarsPanel title={t('bod.hr.byDept')} sub={t('bod.hr.byDeptS')} span={4} rows={DL.map(([k, n]) => ({ k, n, tone: n > 2 ? 'warn' : 'pri', v: String(n) }))} />
      <Chart12 title={t('bod.hr.ch2')} sub={t('bod.hr.ch2s')} span={6} ser={LATE} label={t('bod.hr.turn')} line={6} lineLabel={t('bod.hr.ch2m')} max={9} ymax="" idx={idx} />
      <Chart12 title={t('bod.hr.ch3')} sub={t('bod.hr.ch3s')} span={6} ser={OT} label={t('bod.hr.ch3l')} ymax={t('bod.hr.ch3y')} idx={idx} />
      <section aria-label={t('bod.hr.rank')} className={`${b.card} ${b.span12}`}>
        <div className={b.ph}><h2>{t('bod.hr.rank')}</h2><span>{t('bod.hr.ranks')}</span></div>
        <Table label={t('bod.hr.rank')} columns={cols} rows={[...DR]} rowKey={(r) => r[0]} dense />
      </section>
      <AttnPanel title={t('bod.hr.mile')} sub={t('bod.hr.mileS')} span={6} rows={A.map(([k, app, v, sev, sb]) => ({ k, app, v, sev, s: sb }))} />
      <ListPanel title={t('bod.hr.prod')} sub={t('bod.hr.prodS')} span={6} rows={[[t('bod.hr.p1'), '18,4'], [t('bod.hr.p2'), '42'], [t('bod.hr.p3'), '29'], [t('bod.hr.p4'), t('bod.hr.p4v')]]} />
      <ListPanel title={t('bod.hr.dept')} sub={t('bod.hr.deptS')} span={6} rows={DR.map((r) => [r[0], String(r[1])] as const)} />
      <BarsPanel title={t('bod.hr.ppl')} sub={t('bod.hr.pplS')} span={6} rows={ppl.map(([k, n, tone, sb]) => ({ k, n, tone, v: t('bod.hr.times', { n }), s: sb }))} />
    </div>
  );
}
